import { useTranslation } from 'react-i18next';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import WhatsAppFloat from '../../home/components/WhatsAppFloat';
import SEO from '@/components/SEO';

export default function ProductionPage() {
  const { t } = useTranslation('navigation');

  const solutions = [
    {
      titleKey: 'productionSol1Title', descKey: 'productionSol1Desc', icone: 'ri-calendar-schedule-line',
      details_fr: ['Planification automatique des ordres', 'Gestion des capacités machines', 'Optimisation des ressources humaines', 'Suivi temps réel de la production', 'Alertes de retard automatiques'],
      details_en: ['Automatic order planning', 'Machine capacity management', 'Human resource optimization', 'Real-time production tracking', 'Automatic delay alerts']
    },
    {
      titleKey: 'productionSol2Title', descKey: 'productionSol2Desc', icone: 'ri-shield-check-line',
      details_fr: ['Points de contrôle qualité', 'Traçabilité des lots de production', 'Gestion des non-conformités', 'Certificats qualité automatiques', 'Analyses statistiques qualité'],
      details_en: ['Quality control points', 'Production batch traceability', 'Non-conformity management', 'Automatic quality certificates', 'Quality statistical analysis']
    },
    {
      titleKey: 'productionSol3Title', descKey: 'productionSol3Desc', icone: 'ri-tools-line',
      details_fr: ['Planning maintenance préventive', 'Historique des interventions', 'Gestion des pièces de rechange', 'Suivi des coûts de maintenance', 'Alertes d\'entretien automatiques'],
      details_en: ['Preventive maintenance planning', 'Intervention history', 'Spare parts management', 'Maintenance cost tracking', 'Automatic maintenance alerts']
    },
    {
      titleKey: 'productionSol4Title', descKey: 'productionSol4Desc', icone: 'ri-archive-line',
      details_fr: ['Suivi stocks matières premières', 'Gestion des produits semi-finis', 'Inventaire produits finis', 'Optimisation des approvisionnements', 'Traçabilité complète des lots'],
      details_en: ['Raw materials stock tracking', 'Semi-finished products management', 'Finished products inventory', 'Supply optimization', 'Complete batch traceability']
    },
    {
      titleKey: 'productionSol5Title', descKey: 'productionSol5Desc', icone: 'ri-calculator-line',
      details_fr: ['Calcul coûts de revient', 'Analyse des marges par produit', 'Suivi des coûts matières', 'Coûts main d\'œuvre', 'Reporting financier détaillé'],
      details_en: ['Cost calculation', 'Margin analysis per product', 'Material cost tracking', 'Labor costs', 'Detailed financial reporting']
    },
    {
      titleKey: 'productionSol6Title', descKey: 'productionSol6Desc', icone: 'ri-dashboard-line',
      details_fr: ['KPI production en temps réel', 'Taux de rendement synthétique', 'Analyse des performances', 'Rapports de productivité', 'Alertes de dérive'],
      details_en: ['Real-time production KPIs', 'Synthetic efficiency rate', 'Performance analysis', 'Productivity reports', 'Drift alerts']
    }
  ];

  const isEn = t('home') === 'Home';

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Logiciel Production & Industrie - Alpha Solutions | MES & Gestion Usine"
        description="Solution complète pour production et industrie : planification, contrôle qualité, maintenance, stocks, coûts de revient et tableaux de bord en temps réel."
        keywords="logiciel production Cameroun, MES Douala, gestion usine Afrique, contrôle qualité industrie, ERP industrie"
        canonicalPath="/secteurs/production"
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Logiciel Production & Industrie Alpha Solutions",
          "description": "Solution MES et gestion de production pour industries au Cameroun",
          "brand": { "@type": "Organization", "name": "Alpha Solutions" }
        }}
      />
      <Header />

      <section
        className="relative bg-cover bg-center py-20"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6),rgba(0,0,0,0.6)),url('https://readdy.ai/api/search-image?query=modern%20african%20manufacturing%20facility%20advanced%20production%20management%20systems%20digital%20quality%20control%20technology%20industrial%20plant%20automated%20machinery%20efficient%20production%20workflow&width=1200&height=600&seq=production-hero-v2&orientation=landscape')` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">{t('productionHeroTitle')}</h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">{t('productionHeroDesc')}</p>
          <a href="https://wa.me/237693385267" target="_blank" rel="noopener noreferrer" className="inline-block bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-whatsapp-line mr-2"></i>{t('sectorDemoBtn')}
          </a>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('productionSolutionsTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('productionSolutionsSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((sol, i) => (
              <div key={i} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-slate-700 text-white rounded-full flex items-center justify-center mb-4">
                  <i className={`${sol.icone} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t(sol.titleKey)}</h3>
                <p className="text-gray-600 mb-4">{t(sol.descKey)}</p>
                <ul className="space-y-2">
                  {(isEn ? sol.details_en : sol.details_fr).map((f, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-2 mt-1"></i>
                      <span className="text-sm text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('productionWhyTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'ri-speed-up-line', title: t('productionAdv1Title'), desc: t('productionAdv1Desc') },
              { icon: 'ri-money-dollar-circle-line', title: t('productionAdv2Title'), desc: t('productionAdv2Desc') },
              { icon: 'ri-award-line', title: t('productionAdv3Title'), desc: t('productionAdv3Desc') },
              { icon: 'ri-route-line', title: t('productionAdv4Title'), desc: t('productionAdv4Desc') },
            ].map((adv, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-slate-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${adv.icon} text-2xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{adv.title}</h3>
                <p className="text-gray-600">{adv.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-50 rounded-lg p-8 border-l-4 border-slate-700">
            <div className="flex items-center mb-6">
              <img src="https://readdy.ai/api/search-image?query=professional%20african%20industrial%20manager%20in%20modern%20manufacturing%20facility%20confident%20production%20director%20overseeing%20efficient%20industrial%20operations%20advanced%20technology&width=100&height=100&seq=sofipac-v2&orientation=squarish" alt={t('productionTestiName')} className="w-16 h-16 rounded-full object-cover mr-4" />
              <div>
                <h3 className="text-xl font-bold text-slate-900">{t('productionTestiName')}</h3>
                <p className="text-slate-700">{t('productionTestiRole')}</p>
              </div>
            </div>
            <blockquote className="text-lg text-slate-800 italic mb-4">"{t('productionTestiQuote')}"</blockquote>
            <div className="flex items-center">
              <div className="flex text-yellow-400">{[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill"></i>)}</div>
              <span className="ml-2 text-slate-700 font-medium">{t('productionTestiSince')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">{t('productionCtaTitle')}</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">{t('productionCtaDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://wa.me/237693385267" target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line mr-2"></i>{t('sectorFreeDemo')}
            </a>
            <a href="/solutions" className="bg-white text-slate-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">{t('sectorViewSolutions')}</a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
